<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
  >
    <template v-slot:header="{ ok, cancel }">
      <v-card-title class="screen-title">KCPS向けVNコネクト情報</v-card-title>
      <div class="d-flex flex-row align-start ga-2">
        <template v-if="billingFreeFlg">
          <v-chip size="large" color="info" variant="flat">無料枠</v-chip>
        </template>
        <button
          type="button"
          aria-label="Close"
          class="close ml-5"
          @click="isChanged ? ok() : cancel()"
        >
          ×
        </button>
      </div>
    </template>
    <template v-if="isLoaded" v-slot:body="{ ok, cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <v-sheet v-if="errorStatus">
              <v-sheet
                class="pa-4 text-body-2"
                :color="errorDetailColor"
                rounded
              >
                <v-row>
                  <v-icon :icon="errorIcon" size="30" class="mt-4" />
                  <v-col>
                    <div
                      class="text-subtitle-1 font-weight-bold d-flex align-center pl-2"
                    >
                      エラーが発生しました。
                    </div>
                    <div
                      class="text-subtitle-1 font-weight-bold d-flex align-center pl-2"
                    >
                      ※以下の内容をご確認の上、<a
                        href="https://wvs2document.com/multi-cloud-errors-overview/"
                        target="_blank"
                        rel="noopener noreferrer"
                        >Webマニュアル</a
                      >を参照してください。
                    </div>
                  </v-col>
                  <v-icon
                    :icon="expandedIcon"
                    size="30"
                    @click="isExpanded = !isExpanded"
                    class="d-flex cursor-pointer mt-4"
                  />
                </v-row>
              </v-sheet>
              <v-sheet
                v-if="isExpanded"
                class="pr-2 pl-2 text-body-2"
                color="gray"
                rounded
              >
                <v-col>
                  <div class="text-subtitle-1 font-weight-bold">
                    ステータス：{{ $filter.emptyTo(errorDetail?.id, "-") }}
                  </div>
                  <div class="text-subtitle-1 font-weight-bold reason-message">
                    理由：{{ $filter.emptyTo(errorDetail?.message, "-") }}
                  </div>
                  <div class="text-subtitle-1 font-weight-bold">
                    発生時刻：{{
                      $filter.datetime(errorDetail?.timestamp, { s: true }) ??
                      ""
                    }}
                  </div>
                </v-col>
              </v-sheet>
            </v-sheet>
            <v-row>
              <v-col class="d-flex flex-row ga-2 mt-2 justify-end">
                <v-btn
                  v-if="errorMode.includes('R')"
                  v-permission.config
                  color="primary"
                  @click="VnConnectRetry('R')"
                  >再実行</v-btn
                >
                <v-btn
                  v-if="errorMode.includes('C')"
                  v-permission.config
                  color="primary"
                  @click="VnConnectRetry('C')"
                  >再作成</v-btn
                >
                <v-btn
                  v-if="errorMode.includes('M')"
                  v-permission.config
                  color="primary"
                  @click="showVNConnectModify"
                  >変更</v-btn
                >
                <v-btn
                  v-if="errorMode.includes('D')"
                  v-permission.config
                  color="primary"
                  @click="showVNConnectDelete"
                  >削除</v-btn
                >
              </v-col>
            </v-row>
            <v-row>
              <!-- 基本設定 -->
              <v-col cols="6">
                <v-row>
                  <v-col>
                    <h3>基本設定</h3>
                  </v-col>
                </v-row>
                <div class="ml-2">
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="KCPSユーザーID"
                        :value="cloudDetail.cloudVnConnectCommon.serviceKey"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="回線番号"
                        :value="cloudDetail.enumber"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group label="品目">
                        <template #value>
                          <div class="ml-3">
                            {{
                              cloudDetail.cloudVnConnectCommon.bandwidthType
                            }}({{
                              $filter.bandwidthTo(
                                cloudDetail.cloudVnConnectCommon.bandwidth
                              )
                            }})
                          </div>
                        </template>
                      </app-label-group>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="ロケーション"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectCommon.location,
                            'kcpsLocationV2'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="契約番号(M番号)"
                        :value="cloudDetail.cloudVnConnectCommon.mNumber"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="利用ゾーン"
                        :value="cloudDetail.cloudVnConnectCommon.usageZone"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="プライベートLAN ID"
                        :value="cloudDetail.cloudVnConnectCommon.privateLanId"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="仮想IPアドレス"
                        :value="
                          cloudDetail.cloudVnConnectCommon.virtualIpAddress
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="備考"
                        :value="
                          $filter.emptyTo(
                            cloudDetail.cloudVnConnectCommon.description,
                            '-'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-col>

              <!--接続先VPN/VN -->
              <v-col cols="6">
                <v-row>
                  <v-col>
                    <h3>接続先VPN/VN</h3>
                  </v-col>
                </v-row>
                <div class="ml-2">
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VPN/VNコード"
                        :value="cloudDetail.cloudVnConnectCommon.vpnVnCode"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="仮想ネットワーク(VN)名"
                        :value="
                          cloudDetail.cloudVnConnectCommon.vpnVnCode.startsWith(
                            'B'
                          )
                            ? '-'
                            : cloudDetail.cloudVnConnectCommon.vnName
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="L2/L3"
                        :value="cloudDetail.cloudVnConnectCommon.vnType"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="ルーティング種別"
                        :value="cloudDetail.cloudVnConnectCommon.routingType"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="AS番号"
                        :value="cloudDetail.cloudVnConnectCommon.asNumber"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="MD5キー"
                        :value="
                          $filter.emptyTo(
                            cloudDetail.cloudVnConnectCommon.md5Key,
                            '-'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="Maximum Prefix"
                        :value="cloudDetail.cloudVnConnectCommon.maximumPrefix"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>

            <!-- 広報ルート（独立セクション） -->
            <v-row class="mt-2">
              <v-col cols="12">
                <v-row>
                  <v-col>
                    <h3>広報ルート</h3>
                  </v-col>
                </v-row>
                <div class="ml-2">
                  <!--Connectedルート広報-->
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="Connectedルート広報"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectCommon.publicRelation,
                            'enable'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                  <!--BGP 広報ルート(ルート分割)-->
                  <v-row>
                    <v-col>
                      <app-label-group label="BGP 広報ルート(ルート分割)">
                        <template #value>
                          <div
                            v-if="sortedSplitRouteList.length"
                            class="d-flex flex-wrap address-scroll"
                          >
                            <span
                              class="ml-3"
                              v-for="(item, index) in sortedSplitRouteList"
                              :key="index"
                            >
                              {{ item }}
                            </span>
                          </div>
                          <span v-else class="ml-3">-</span>
                        </template>
                      </app-label-group>
                    </v-col>
                  </v-row>
                  <!--BGP 広報ルート(Connected以外) / (Connected以外のネクストホップ)-->
                  <v-row class="mt-2">
                    <v-col cols="12">
                      <app-label-group label="BGP 広報ルート(Connected以外)" />
                    </v-col>
                  </v-row>
                  <div class="ml-2">
                    <v-row>
                      <v-col cols="6" class="address-title">
                        <app-badge
                          item="LANアドレス"
                          :isRequired="false"
                          :isInfo="false"
                        />
                      </v-col>
                      <v-col cols="6" class="address-title">
                        <app-badge
                          item="ネクストホップアドレス"
                          :isRequired="false"
                          :isInfo="false"
                        />
                      </v-col>
                    </v-row>
                    <div class="address-scroll d-flex">
                      <div class="w-50 d-flex flex-column">
                        <template v-if="sortedConnectedPairs.length">
                          <span
                            class="ml-3"
                            v-for="(pair, index) in sortedConnectedPairs"
                            :key="index"
                          >
                            {{ pair.lan }}
                          </span>
                        </template>
                        <span v-else class="ml-3">-</span>
                      </div>
                      <div class="w-50 d-flex flex-column">
                        <template v-if="sortedConnectedPairs.length">
                          <span
                            class="ml-5"
                            v-for="(pair, index) in sortedConnectedPairs"
                            :key="index"
                          >
                            {{ pair.nextHop }}
                          </span>
                        </template>
                        <span v-else class="ml-5">-</span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <!--回線情報（Act）-->
              <v-col cols="6">
                <v-row>
                  <v-col>
                    <h3>回線情報(Act)</h3>
                  </v-col>
                </v-row>
                <div class="ml-2">
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VNコネクト番号"
                        :value="cloudDetail.cloudVnConnectAct.wnumber"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VNコネクト名"
                        :value="cloudDetail.cloudVnConnectAct.vnConnectName"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VLAN ID(Inner)"
                        :value="cloudDetail.cloudVnConnectAct.vlanIdInner"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VLAN ID(Outer)"
                        :value="cloudDetail.cloudVnConnectAct.vlanIdOuter"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="有効/無効"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectAct.isVnConnectStatus,
                            'enable'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="IF状態"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectAct.ifStatus,
                            'cloudVn_ifStatus'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-col>

              <!-- 回線情報（Sby） -->
              <v-col cols="6">
                <v-row>
                  <v-col>
                    <h3>回線情報(Sby)</h3>
                  </v-col>
                </v-row>
                <div class="ml-2">
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VNコネクト番号"
                        :value="cloudDetail.cloudVnConnectSby.wnumber"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VNコネクト名"
                        :value="cloudDetail.cloudVnConnectSby.vnConnectName"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VLAN ID(Inner)"
                        :value="cloudDetail.cloudVnConnectSby.vlanIdInner"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="VLAN ID(Outer)"
                        :value="cloudDetail.cloudVnConnectSby.vlanIdOuter"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="有効/無効"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectSby.isVnConnectStatus,
                            'enable'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="IF状態"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectSby.ifStatus,
                            'cloudVn_ifStatus'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <!-- ルーティング情報（Act） -->
              <v-col cols="6">
                <v-row>
                  <v-col>
                    <h3>ルーティング情報(Act)</h3>
                  </v-col>
                </v-row>
                <div class="ml-2">
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="WANアドレス"
                        :value="cloudDetail.cloudVnConnectAct.wanAddress"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="ピアIPアドレス"
                        :value="cloudDetail.cloudVnConnectAct.peerIpAddress"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="プライベートIPアドレス"
                        :value="cloudDetail.cloudVnConnectAct.privateIpAddress"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="Local Preference"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectAct.localPreference,
                            'localPreference'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="MED"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectAct.med,
                            'med'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-col>

              <!-- ルーティング情報（Sby） -->
              <v-col cols="6">
                <v-row>
                  <v-col>
                    <h3>ルーティング情報(Sby)</h3>
                  </v-col>
                </v-row>
                <div class="ml-2">
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="WANアドレス"
                        :value="cloudDetail.cloudVnConnectSby.wanAddress"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="ピアIPアドレス"
                        :value="cloudDetail.cloudVnConnectSby.peerIpAddress"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="プライベートIPアドレス"
                        :value="cloudDetail.cloudVnConnectSby.privateIpAddress"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="Local Preference"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectSby.localPreference,
                            'localPreference'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="MED"
                        :value="
                          $filter.enumTo(
                            cloudDetail.cloudVnConnectSby.med,
                            'med'
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <!-- カスタム経路集約情報 -->
              <v-col cols="12">
                <v-row>
                  <v-col>
                    <h3>カスタム経路集約情報</h3>
                  </v-col>
                </v-row>
                <div class="ml-2">
                  <v-row>
                    <v-col>
                      <app-label-group
                        label="経路集約"
                        :value="
                          cloudDetail.cloudVnConnectCommon.routeAggregation
                        "
                      />
                    </v-col>
                  </v-row>
                  <v-row
                    v-if="
                      cloudDetail.cloudVnConnectCommon.routeAggregation !==
                      'OFF'
                    "
                  >
                    <v-col>
                      <app-label-group label="経路集約アドレス">
                        <template #value>
                          <template
                            v-if="
                              cloudDetail.cloudVnConnectCommon
                                .routeAggregation === 'CUSTOM'
                            "
                          >
                            <div class="d-flex flex-wrap">
                              <span
                                class="ml-3"
                                v-for="(
                                  ip, index
                                ) in sortedRouteAggregationAddressList"
                                :key="index"
                              >
                                {{ ip }}
                              </span>
                            </div>
                          </template>
                          <template
                            v-else-if="
                              cloudDetail.cloudVnConnectCommon
                                .routeAggregation === 'ON'
                            "
                          >
                            <span class="ml-3">0.0.0.0/0</span>
                          </template>
                        </template>
                      </app-label-group>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-6" v-if="isShowOperationTabs">
              <v-col>
                <v-tabs
                  v-model="tabValue"
                  @update:modelValue="alertClear"
                  hide-slider
                  class="border-s-sm border-t-sm border-e-sm"
                >
                  <!--運用コマンド(Act)-->
                  <v-tab text="運用コマンド(Act)" value="operationCommandAct" />
                  <!--運用コマンド(Sby)-->
                  <v-tab text="運用コマンド(Sby)" value="operationCommandSby" />
                  <!--トラフィック-->
                  <v-tab
                    text="トラフィック"
                    value="traffic"
                    v-if="!isFutureDayContract && contractSummary.isTrafficInfo"
                  />
                  <v-tab text="設定上限値" value="upperLimit" />
                </v-tabs>
                <v-tabs-window
                  v-model="tabValue"
                  class="border-s-sm border-b-sm border-e-sm"
                >
                  <!--運用コマンド(Act)-->
                  <v-tabs-window-item value="operationCommandAct">
                    <operation-command
                      ref="operationCommandAct"
                      lineCategory="CLOUD"
                      :wnumber="cloudDetail.cloudVnConnectAct.wnumber"
                      :ifStatus="cloudDetail.cloudVnConnectAct.ifStatus"
                      :key-id="cloudDetail.cloudVnKcpsKeyId"
                      kcps-version="V2"
                      :cloud-line-seq="cloudLineSeq"
                      @executed="executed"
                    ></operation-command>
                  </v-tabs-window-item>

                  <!--運用コマンド(Sby)-->
                  <v-tabs-window-item value="operationCommandSby">
                    <operation-command
                      ref="operationCommandSby"
                      lineCategory="CLOUD"
                      :wnumber="cloudDetail.cloudVnConnectSby!.wnumber"
                      :ifStatus="cloudDetail.cloudVnConnectSby!.ifStatus"
                      :key-id="cloudDetail.cloudVnKcpsKeyId"
                      kcps-version="V2"
                      :cloud-line-seq="cloudLineSeq"
                      @executed="executed"
                    ></operation-command>
                  </v-tabs-window-item>

                  <!--トラフィック-->
                  <v-tabs-window-item value="traffic">
                    <traffic
                      line-type="MULTICLOUD"
                      :line="{
                        wnumberMainAct: cloudDetail.cloudVnConnectAct.wnumber,
                        wnumberMainSby: cloudDetail.cloudVnConnectSby.wnumber,
                      }"
                    />
                  </v-tabs-window-item>

                  <!--設定上限値-->
                  <v-tabs-window-item value="upperLimit">
                    <upper-limit
                      upperLimitManageUnit="MULTI_CLOUD"
                      :wnumber="cloudDetail.cloudVnConnectAct.wnumber"
                      :cloud-line-seq="cloudLineSeq"
                    />
                  </v-tabs-window-item>
                </v-tabs-window>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn
              @click="isChanged ? ok() : cancel()"
              variant="outlined"
              color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { CloudVnKcpsDetailV2 } from "@/apis/CloudVnKcpsApi";
import MultiCloudKcps3VnConnectionDeleteConfirm from "@/modals/multicloud/kcps3/v2/V2MultiCloudKcps3VnConnectionDeleteConfirm.vue";
import { mapState } from "vuex";
import type { CloudLine } from "@/apis/CloudLineApi";
import type { ContractInfo } from "@/apis/ContractApi";
import Traffic from "@/components/operation-tool/Traffic.vue";
import OperationCommand from "@/components/operation-tool/OperationCommand.vue";
import UpperLimit from "@/components/operation-tool/UpperLimit.vue";
import MultiCloudKcps3VnLineEdit from "@/modals/multicloud/kcps3/v2/V2MultiCloudKcps3VnLineEdit.vue";
import sortCompare from "@/services/SortService";
import { ApiForbiddenError } from "@/error/errors";
import { parseErrorDetail } from "@/services/ErrorDetailParser";

export default defineComponent({
  name: "MultiCloudKcps3VnConnectionReference",
  components: { OperationCommand, Traffic, UpperLimit },
  props: {
    wnumber: {
      type: String as PropType<string>,
      required: true,
    },
    cloudLineSeq: {
      type: String as PropType<string>,
      required: true,
    },
    billingFreeFlg: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    // クラウド回線の帯域最大値(品目の上限値制御用)
    cloudLineMaxBandwidth: {
      type: Object as PropType<CloudLine["bandwidth"]>,
      required: true,
    },
    remainingBandwidth: {
      type: Number as PropType<number>,
      required: true,
    },
  },
  data() {
    return {
      cloudDetail: {} as CloudVnKcpsDetailV2,
      isLoaded: false,
      isChanged: false,
      isExpanded: true,
      isShowOperationTabs: true,
      tabValue: "operationCommandAct" as
        | "operationCommandSby"
        | "traffic"
        | "upperLimit",
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  computed: {
    ...mapState("user", {
      contractInfo: "contractInfo",
      contractSummary: "contractSummary",
    }),
    /** 開始日が未来日の場合,true*/
    isFutureDayContract(): boolean {
      return !this.contractInfo.contractList.find(
        (e: ContractInfo["contractList"][0]) =>
          e.enumber === this.cloudDetail.enumber
      );
    },
    /** エラーステータス */
    errorStatus(): boolean {
      const status = this.cloudDetail.cloudVnConnectCommon.provStatus;
      return (
        status === "RETRYABLE" ||
        status === "CREATE_FAILED" ||
        status === "FAILED" ||
        status === "PENDING_DELETE" ||
        status === "DELETE_RETRYABLE"
      );
    },
    /** エラー時のアイコンを選択 */
    errorIcon(): string {
      const status = this.cloudDetail.cloudVnConnectCommon.provStatus;
      if (status === "RETRYABLE" || status === "DELETE_RETRYABLE") {
        return "$mdiCloseCircle";
      } else if (
        status === "FAILED" ||
        status === "PENDING_DELETE" ||
        status === "CREATE_FAILED"
      ) {
        return "$mdiAlertCircle";
      }
      return "";
    },
    /** 展開アイコン */
    expandedIcon(): string {
      return this.isExpanded ? "$mdiChevronUp" : "$mdiChevronDown";
    },
    /** エラー詳細のカラーを選択 */
    errorDetailColor(): string {
      const status = this.cloudDetail.cloudVnConnectCommon.provStatus;
      if (
        status === "RETRYABLE" ||
        status === "DELETE_RETRYABLE" ||
        status === "CREATE_FAILED" ||
        status === "PENDING_DELETE"
      ) {
        return "error";
      } else if (status === "FAILED") {
        return "warning";
      }
      return "";
    },
    /**  エラー状態によるボタン表示関係
     *  R：再実行可能状態
     *  D：削除可能状態
     *  M：変更可能状態
     */
    errorMode(): string {
      const status = this.cloudDetail.cloudVnConnectCommon.provStatus;
      if (status === "RETRYABLE") {
        return "RD";
      } else if (status === "PENDING_DELETE") {
        return "D";
      } else if (status === "DELETE_RETRYABLE") {
        return "R";
      } else if (status === "CREATE_FAILED") {
        return "RD";
      }
      return "MD";
    },
    /** errorDetail JSONから表示用ステータス詳細を抽出 */
    errorDetail(): {
      id: string | null;
      message: string | null;
      timestamp: string | null;
    } | null {
      return parseErrorDetail(
        this.cloudDetail.cloudVnConnectCommon.errorDetail,
        "KCPS Server Error"
      );
    },
    /** BGP 広報ルート(ルート分割) を数値ソートした一覧 */
    sortedSplitRouteList(): string[] {
      const list =
        this.cloudDetail.cloudVnConnectCommon?.publicRelationSplitRouteList ??
        [];
      return list.sortIpAddress();
    },
    /** BGP 広報ルート(Connected以外) を LAN アドレスで数値ソートしたペア一覧 */
    sortedConnectedPairs(): { lan: string; nextHop: string }[] {
      const lanList =
        this.cloudDetail.cloudVnConnectCommon
          ?.publicRelationConnectedExceptList;
      const nextHopList =
        this.cloudDetail.cloudVnConnectCommon?.publicRelationNextHopList;
      if (!lanList?.length || !nextHopList?.length) return [];
      return lanList
        .map((lan, i) => ({ lan, nextHop: nextHopList[i] ?? "" }))
        .sort((a, b) => sortCompare(a, b, [["lan", "asc"]]));
    },
    /** 経路集約アドレス（数値昇順ソート済み） */
    sortedRouteAggregationAddressList(): string[] {
      const list =
        this.cloudDetail.cloudVnConnectCommon?.routeAggregationAddressList ??
        [];
      return list.sortIpAddress();
    },
  },
  methods: {
    async load() {
      // cloudDetailは必ず取得
      this.cloudDetail = await this.$api.cloudVnKcps.getVnConnectV2(
        this.wnumber
      );
      // 403チェックの前にリセットする（再ロード時に前回のfalseを引き継がないようにするため）
      this.isShowOperationTabs = true;
      try {
        // クラコン側にデータが無い場合に運用コマンドAPIが403を返すため、事前に403判定する目的で呼び出す
        // 正常時はOperationCommand内で再度同APIが呼ばれるが、403回避のためやむを得ない
        await this.$api.nwOperationTool.getCommandResultsVN(
          "CLOUD",
          this.wnumber
        );
      } catch (error) {
        // 403の場合は運用コマンドタブを非表示にする（表示したままだとエラーになるため）
        if (error instanceof ApiForbiddenError) {
          this.isShowOperationTabs = false;
        } else {
          // 403以外は共通エラーハンドラで処理
          throw error;
        }
      }
    },
    /** 再実行 */
    async VnConnectRetry(displayType: "R" | "C") {
      await this.alertClear();
      if (displayType === "R") {
        await this.$confirm(`VNコネクト再実行を行います。よろしいですか？`);
      } else if (displayType === "C") {
        await this.$confirm(`VNコネクト再作成を行います。よろしいですか？`);
      }
      await this.$api.cloudVnKcps.cloudVnKcpsRetryV2(
        this.cloudLineSeq,
        this.cloudDetail.cloudVnConnectAct.wnumber,
        {
          cloudVnKcpsKeyId: this.cloudDetail.cloudVnKcpsKeyId,
        }
      );
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    async showVNConnectDelete() {
      this.alertClear();
      // 削除確認モーダルの表示
      await this.$modal.show(MultiCloudKcps3VnConnectionDeleteConfirm, {
        cloudVnKcpsDetail: this.cloudDetail,
        billingFreeFlg: this.billingFreeFlg,
      });
      // 削除処理
      await this.$api.cloudVnKcps.deleteVnConnectV2(this.cloudLineSeq, {
        cloudVnConnectList: [
          this.cloudDetail.cloudVnConnectAct.wnumber,
          this.cloudDetail.cloudVnConnectSby.wnumber,
        ],
        cloudVnKcpsKeyId: this.cloudDetail.cloudVnKcpsKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** 変更 */
    async showVNConnectModify() {
      await this.alertClear();
      await this.$modal.show(MultiCloudKcps3VnLineEdit, {
        cloudLineSeq: this.cloudLineSeq,
        cloudVnKcpsDetail: this.cloudDetail,
        billingFreeFlg: this.billingFreeFlg,
        cloudLineMaxBandwidth: this.cloudLineMaxBandwidth,
        remainingBandwidth: this.remainingBandwidth,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /**
     * コマンド実行後の処理
     * IF設定反映の場合、一覧画面へ戻る
     * @param eventType 実行されたコマンドの種類
     */
    async executed(eventType: string) {
      if (eventType === "IF_STATE") {
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
      } else {
        this.isChanged = true;
        await this.load();
      }
    },
    /** エラーメッセージを初期化する */
    async alertClear() {
      if (this.$refs.operationCommandAct) {
        (
          this.$refs.operationCommandAct as InstanceType<
            typeof OperationCommand
          >
        ).alertClear();
      }
      if (this.$refs.operationCommandSby) {
        (
          this.$refs.operationCommandSby as InstanceType<
            typeof OperationCommand
          >
        ).alertClear();
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.address-title :deep(.badge-label) {
  font-size: 0.75rem !important;
}
.address-scroll {
  max-height: 107px;
  overflow-y: auto;
}
.reason-message {
  max-height: 330px;
  overflow-y: auto;
}
</style>
